import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Leaf, CheckSquare, Warning, Star, TrendUp, Clock } from "@phosphor-icons/react";
import { StatusBadge, PriorityBadge } from "../components/crm/Badges";
import { fmtDate, fmtDateTime } from "../lib/utils";
import DateRangeFilter from "../components/crm/DateRangeFilter";

const fmtEuro = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

function Kpi({ label, value, Icon, tint = "#1A3A21", testid, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</div>
          <div className="mt-1 font-display text-3xl font-bold">{value}</div>
          {sub && <div className="text-[11px] text-gray-500 mt-1 leading-tight min-h-[1.75rem]">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${tint}14`, color: tint }}>
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function AgentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [revRange, setRevRange] = useState({ preset: "this_month", start_date: "", end_date: "" });

  useEffect(() => {
    api.get("/dashboard/agent").then(({ data }) => setData(data));
    api.get("/activities", { params: { status: "Open" } }).then(({ data }) => setActivities(data));
  }, []);

  useEffect(() => {
    const params = { date_preset: revRange.preset };
    if (revRange.preset === "custom") {
      if (revRange.start_date) params.start_date = revRange.start_date;
      if (revRange.end_date) params.end_date = revRange.end_date;
    }
    api.get("/dashboard/agent/revenue", { params }).then(({ data }) => setRevenue(data)).catch(() => setRevenue(null));
  }, [revRange]);

  if (!data) return <div className="text-gray-500">Loading...</div>;

  const todayISO = new Date().toISOString().slice(0, 10);
  const now = new Date();
  const todaysActivities = activities.filter((a) => (a.next_follow_up_date || "").startsWith(todayISO)).slice(0, 6);
  const overdueActivities = activities.filter((a) => a.next_follow_up_date && new Date(a.next_follow_up_date) < now).slice(0, 6);

  const activityTitle = (a) => {
    const map = {
      CALL: "Call", EMAIL: "Email", WHATSAPP: "WhatsApp", SAMPLE: "Sample",
      ORDER: "Order", MEETING: "Meeting", NOTE: "Note",
    };
    return `${map[a.activity_type] || a.activity_type} · ${a.title || ""}`;
  };

  return (
    <div className="space-y-8" data-testid="agent-dashboard">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">
          Agent overview
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
          Hi {user?.name?.split(" ")[0] || "there"}, let&apos;s close some deals.
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="My Leads" value={data.total_leads} Icon={Leaf} tint="#1A3A21" testid="kpi-my-leads"
          sub="All assigned" />
        <Kpi label="New" value={data.by_status.New || 0} Icon={Leaf} tint="#0EA5E9" testid="kpi-new"
          sub="No activity yet" />
        <Kpi label="Qualified" value={data.by_status.Qualified || 0} Icon={TrendUp} tint="#0EA5E9" testid="kpi-qualified"
          sub="Commercial activity" />
        <Kpi label="Customers" value={data.by_status.Customer || 0} Icon={CheckSquare} tint="#10B981" testid="kpi-customers"
          sub="Recent ORDER" />
        <Kpi label="Sleepy" value={data.by_status.Sleepy || 0} Icon={Clock} tint="#F59E0B" testid="kpi-sleepy"
          sub="Idle 30+ days" />
        <Kpi label="Lost" value={data.by_status.Lost || 0} Icon={Warning} tint="#EF4444" testid="kpi-lost"
          sub="Idle 90+ days" />
        <Kpi label="Open Activities" value={data.open_activities ?? 0} Icon={CheckSquare} tint="#E1A104" testid="kpi-open-activities"
          sub="Still to complete" />
        <Kpi label="Overdue Activities" value={data.overdue_activities ?? 0} Icon={Warning} tint="#EF4444" testid="kpi-overdue-activities"
          sub="Past due date" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="todays-activities">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Today&apos;s Activities</h3>
            <Link to="/activities" className="text-xs text-[#1A3A21] font-semibold hover:underline">View all</Link>
          </div>
          {todaysActivities.length ? todaysActivities.map((t) => (
            <Link to={`/leads/${t.lead_id}`} key={t.id}
              className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <PriorityBadge priority={t.priority || "B"} />
                <div>
                  <div className="text-sm font-medium">{activityTitle(t)}</div>
                  <div className="text-[11px] text-gray-500">{fmtDateTime(t.next_follow_up_date)}</div>
                </div>
              </div>
            </Link>
          )) : <div className="text-sm text-gray-500 py-4">No activities scheduled for today.</div>}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="overdue-activities">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Overdue</h3>
            <Link to="/activities" className="text-xs text-[#1A3A21] font-semibold hover:underline">View all</Link>
          </div>
          {overdueActivities.length ? overdueActivities.map((t) => (
            <Link to={`/leads/${t.lead_id}`} key={t.id}
              className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-red-50">
              <div className="flex items-center gap-3">
                <PriorityBadge priority={t.priority || "B"} />
                <div>
                  <div className="text-sm font-medium">{activityTitle(t)}</div>
                  <div className="text-[11px] text-red-600">Due {fmtDate(t.next_follow_up_date)}</div>
                </div>
              </div>
            </Link>
          )) : <div className="text-sm text-gray-500 py-4">No overdue items. </div>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="my-revenue">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Revenue</div>
            <h3 className="font-display text-xl font-bold tracking-tight">My Revenue</h3>
          </div>
          <DateRangeFilter
            preset={revRange.preset}
            startDate={revRange.start_date}
            endDate={revRange.end_date}
            onChange={setRevRange}
            testidPrefix="agent-revenue-range"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div className="border border-gray-200 rounded-md p-4" data-testid="my-revenue-total">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Revenue</div>
            <div className="font-display text-2xl font-bold mt-1">{revenue ? fmtEuro(revenue.total_revenue) : "—"}</div>
          </div>
          <div className="border border-gray-200 rounded-md p-4" data-testid="my-revenue-order-count">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">ORDER Count</div>
            <div className="font-display text-2xl font-bold mt-1">{revenue?.order_count ?? "—"}</div>
          </div>
          <div className="border border-gray-200 rounded-md p-4" data-testid="my-revenue-aov">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Average Order Value</div>
            <div className="font-display text-2xl font-bold mt-1">{revenue ? fmtEuro(revenue.average_order_value) : "—"}</div>
          </div>
          <div className="border border-[#E1A104] bg-[#E1A104]/10 rounded-md p-4" data-testid="this-month-target">
            <div className="text-xs uppercase tracking-wider text-[#1A3A21] font-medium">This Month Target</div>
            <div className="font-display text-lg font-bold mt-1">
              {revenue ? fmtEuro(revenue.this_month_target.this_month_revenue) : "—"}
              <span className="text-sm text-gray-600"> / {revenue ? fmtEuro(revenue.this_month_target.target_amount) : "—"}</span>
            </div>
            <div className="mt-2 h-1.5 bg-white/70 rounded">
              <div className="h-1.5 rounded bg-[#1A3A21]"
                style={{ width: `${Math.min(revenue?.this_month_target?.progress_percent || 0, 100)}%` }} />
            </div>
            <div className="text-[10px] text-gray-600 mt-1">
              {revenue ? `${(revenue.this_month_target.progress_percent || 0).toFixed(1)}% of fixed €20.000 target` : ""}
            </div>
          </div>
        </div>

        <div data-testid="my-revenue-top-leads">
          <h4 className="font-display text-sm font-bold mb-3">Top 10 Leads by Revenue</h4>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Company</th>
                  <th className="text-right py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Revenue</th>
                  <th className="text-right py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Orders</th>
                  <th className="text-left py-2 px-3 font-semibold uppercase tracking-wider text-gray-500">Last ORDER</th>
                </tr>
              </thead>
              <tbody>
                {revenue?.top_leads_by_revenue?.length ? revenue.top_leads_by_revenue.map((r) => (
                  <tr key={r.lead_id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <Link to={`/leads/${r.lead_id}`} className="text-[#1A3A21] font-medium hover:underline">
                        {r.company_name}
                      </Link>
                    </td>
                    <td className="py-2 px-3 text-right font-semibold">{fmtEuro(r.revenue)}</td>
                    <td className="py-2 px-3 text-right">{r.order_count}</td>
                    <td className="py-2 px-3 text-gray-600">{r.last_order_date || "—"}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={4} className="py-4 px-3 text-center text-gray-500">No revenue in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="recently-assigned">
        <h3 className="font-display text-lg font-bold mb-4">Recently Updated Leads</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.recently_assigned.map((l) => (
            <Link to={`/leads/${l.id}`} key={l.id}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:border-[#1A3A21]">
              <div>
                <div className="text-sm font-semibold text-gray-900">{l.company_name}</div>
                <div className="text-xs text-gray-500">{l.city} · {l.country}</div>
              </div>
              <div className="flex items-center gap-2">
                <PriorityBadge priority={l.priority} />
                <StatusBadge status={l.lead_status} />
              </div>
            </Link>
          ))}
          {!data.recently_assigned.length && (
            <div className="text-sm text-gray-500">No recent leads.</div>
          )}
        </div>
      </div>
    </div>
  );
}
